// Checkbox-per-row selection state for a list screen — shared by
// CustomersPage, CatalogPage, and UsersPage (customers/products/users
// multi-select bulk-actions task). Pure client-side UI state, no API calls
// here; a page wires `selectedIds` into whatever bulk-action call it makes.
//
// `rows` is the CURRENT page of data (already filtered/searched) — the
// "select all" checkbox always means "every row visible right now", not
// some separate server-side "select the whole table" concept this task
// never asked for.

import { useCallback, useMemo, useState } from 'react';

export function useRowSelection(rows, getId = (row) => row.id) {
  const [selected, setSelected] = useState(() => new Set());

  const ids = useMemo(() => rows.map(getId), [rows, getId]);
  const selectedIds = useMemo(() => ids.filter((id) => selected.has(id)), [ids, selected]);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggle = useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const everyRowSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      return everyRowSelected ? new Set() : new Set(ids);
    });
  }, [ids]);

  const clear = useCallback(() => setSelected(new Set()), []);
  const isSelected = useCallback((id) => selected.has(id), [selected]);

  return {
    selectedIds,
    count: selectedIds.length,
    allSelected,
    someSelected,
    toggle,
    toggleAll,
    clear,
    isSelected,
  };
}

export default useRowSelection;
